/**
 * @file Companies.js
 * @description Page component for managing company directory, employer records, blocked company lists, and associated check statistics.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatName } from '../utils/formatDate';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DataTable from 'react-data-table-component';
import {
  getCompanies, createCompany, updateCompany, deleteCompany,
  blockCompany, unblockCompany, getChequeCounts, fullSyncCompanies,
} from '../api/services';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

const DEFAULT = { name:'', address:'', phone:'', email:'', notes:'' };
const BLOCK_REASONS = ['Fraud / Suspicious activity', 'Bad cheques', 'Account closed', 'Blacklisted', 'Other'];

/**
 * Formats 10-digit phone string into (XXX) XXX-XXXX layout.
 * @param {string} val - Raw input phone.
 * @returns {string} Formatted phone.
 */
const formatPhone = (val) => {
  const d = val.replace(/\D/g,'').slice(0,10);
  if (!d.length) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
};

/* ── DataTable styles ──────────────────────────────────────── */
const dtStyles = {
  headRow:   { style: { backgroundColor:'var(--gray-4)', borderBottom:'1px solid var(--gray-3)', minHeight:'42px' } },
  headCells: { style: { fontSize:'11.5px', fontWeight:'700', color:'var(--gray-1)', textTransform:'uppercase', letterSpacing:'0.6px', paddingLeft:'16px', paddingRight:'12px' } },
  rows:      { style: { fontSize:'13.5px', color:'var(--dark)', borderBottom:'1px solid var(--gray-4)', minHeight:'52px', '&:hover':{ backgroundColor:'rgba(26,122,74,0.04)' } } },
  cells:     { style: { paddingLeft:'16px', paddingRight:'12px' } },
  pagination:{ style: { borderTop:'1px solid var(--gray-3)', fontSize:'13px', color:'var(--gray-1)', minHeight:'52px' }, pageButtonsStyle:{ borderRadius:'6px', color:'var(--dark)', fill:'var(--gray-1)', '&:hover:not(:disabled)':{ backgroundColor:'var(--gray-4)' } } },
  noData:    { style: { padding:'48px', color:'var(--gray-1)', fontSize:'14px' } },
  progress:  { style: { padding:'48px' } },
};

/**
 * Component for displaying cheque and customer count badges for a company row.
 * @component
 * @param {Object} props
 * @param {string} props.companyId - ID of target company.
 * @param {string} props.companyName - Name of target company.
 * @param {Array} [props.customers] - List of associated customers.
 * @param {number|null} props.chequeCount - Number of checks or null if loading.
 * @param {Function} props.onClick - Click handler for expanding stats.
 * @param {boolean} props.expanded - Whether stats section is expanded.
 */
function CompanyStatsBadge({ companyId, companyName, customers = [], chequeCount, onClick, expanded }) {
  const customerCount = customers.length;
  const loading = chequeCount === null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
      {/* Cheque count */}
      <span
        title={`${chequeCount ?? '…'} cheques from this company`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          background: '#eff6ff', color: '#1d4ed8',
          border: '1px solid #bfdbfe',
          borderRadius: 20, padding: '2px 8px',
          fontSize: 11.5, fontWeight: 700,
          opacity: loading ? 0.5 : 1,
        }}
      >
        📄 {chequeCount ?? '…'}
      </span>

      {/* Customer count — clickable only if > 0 */}
      <button
        onClick={e => { e.stopPropagation(); if (customerCount > 0) onClick(); }}
        disabled={customerCount === 0}
        title={customerCount > 0
          ? `${customerCount} customer${customerCount !== 1 ? 's' : ''} — click to view`
          : 'No customers linked to this company'
        }
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          background: expanded ? 'var(--primary)' : (customerCount > 0 ? 'var(--primary-light)' : 'var(--gray-4)'),
          color: expanded ? 'white' : (customerCount > 0 ? 'var(--primary)' : 'var(--gray-1)'),
          border: '1px solid',
          borderColor: expanded ? 'var(--primary)' : (customerCount > 0 ? 'rgba(26,122,74,0.3)' : 'var(--gray-3)'),
          borderRadius: 20, padding: '2px 8px',
          fontSize: 11.5, fontWeight: 700,
          cursor: customerCount > 0 ? 'pointer' : 'default',
          transition: 'all 0.15s',
        }}
      >
        👥 {customerCount}
        {customerCount > 0 && (
          <span style={{ fontSize: 9, opacity: 0.7 }}>{expanded ? '▲' : '▼'}</span>
        )}
      </button>
    </div>
  );
}

/* ── Expanded Customer Panel ─────────────────────────────────── */
function CustomerPanel({ customers = [] }) {
  const navigate = useNavigate();

  if (customers.length === 0) return null;

  return (
    <div style={{
      background: 'linear-gradient(to bottom, #f0fdf4, #f8fafc)',
      borderTop: '2px solid rgba(26,122,74,0.15)',
      padding: '12px 20px 14px',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>
        👥 {customers.length} Linked Customer{customers.length !== 1 ? 's' : ''} — Employer on File
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {customers.map(c => {
          const name = formatName(c.firstName, c.lastName, c.middleInitial);
          const phone = c.contacts?.find(ct => ct.type === 'Phone' || ct.type === 'Mobile')?.value;
          const initials = `${c.firstName?.[0] || ''}${c.lastName?.[0] || ''}`.toUpperCase();
          return (
            <button
              key={c._id}
              onClick={() => navigate(`/customers/${c._id}`)}
              style={{
                display: 'flex', alignItems: 'center', gap: 9,
                background: 'white', border: '1px solid var(--gray-3)',
                borderRadius: 10, padding: '7px 12px 7px 8px',
                cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.12s', minWidth: 180,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-light)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-3)'; e.currentTarget.style.background = 'white'; }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--primary)', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 800, flexShrink: 0,
              }}>{initials}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--dark)', lineHeight: 1.3 }}>{name}</div>
                {phone && <div style={{ fontSize: 11, color: 'var(--gray-1)', marginTop: 1 }}>{formatPhone(phone)}</div>}
              </div>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--primary)', opacity: 0.7 }}>→</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * CompanyModal dialog component for creating or editing company records.
 * @component
 * @param {Object} props
 * @param {string|null} props.editId - ID of company being edited or null for new.
 * @param {Object} props.form - Form data state object.
 * @param {Function} props.setF - Setter function for form fields.
 * @param {Function} props.onClose - Modal close handler.
 * @param {Function} props.onSave - Save action handler.
 * @param {boolean} props.saving - Saving spinner state.
 */
function CompanyModal({ editId, form, setF, onClose, onSave, saving }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,25,35,0.6)', backdropFilter:'blur(4px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:20, width:520, maxWidth:'100%', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 60px rgba(0,0,0,0.28)' }}>
        <div style={{ background:'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius:'20px 20px 0 0', padding:'22px 28px 18px', color:'#fff' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:28 }}>{editId ? '✏️' : '🏢'}</span>
            <div>
              <div style={{ fontSize:19, fontWeight:800 }}>{editId ? 'Edit Company' : 'Add Company'}</div>
              <div style={{ fontSize:12.5, opacity:.85, marginTop:3 }}>This company can be selected as Employer or Maker/Payer.</div>
            </div>
          </div>
        </div>
        <div style={{ padding:'24px 28px', display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#2563eb', marginBottom:6 }}>Company Name *</label>
            <input className="form-control" value={form.name} placeholder="e.g. Orange Grove Retail Store" onChange={e => setF('name', e.target.value)} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#64748b', marginBottom:5 }}>Phone</label>
              <input className="form-control" value={form.phone} placeholder="(555) 000-0000" inputMode="numeric"
                onKeyDown={e => { if (['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key)) return; if (!/^\d$/.test(e.key)) e.preventDefault(); }}
                onChange={e => setF('phone', formatPhone(e.target.value))} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#64748b', marginBottom:5 }}>Email</label>
              <input className="form-control" value={form.email} placeholder="company@example.com" onChange={e => setF('email', e.target.value)} />
            </div>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#64748b', marginBottom:5 }}>Address</label>
            <input className="form-control" value={form.address} placeholder="Company address" onChange={e => setF('address', e.target.value)} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#64748b', marginBottom:5 }}>Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} placeholder="Internal notes…" onChange={e => setF('notes', e.target.value)} />
          </div>
        </div>
        <div style={{ padding:'14px 28px 22px', display:'flex', gap:10, justifyContent:'flex-end', borderTop:'1px solid #f1f5f9' }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving || !form.name.trim()}>
            {saving ? '⏳ Saving…' : editId ? '💾 Update' : '🏢 Add Company'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * BlockModal component for selecting block reasons and restricting company operations.
 * @component
 * @param {Object} props
 * @param {Object} props.company - Target company object.
 * @param {Function} props.onClose - Modal close handler.
 * @param {Function} props.onBlock - Block submission handler.
 * @param {boolean} props.saving - Saving state.
 */
function BlockModal({ company, onClose, onBlock, saving }) {
  const [reason, setReason] = useState('Fraud / Suspicious activity');
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,25,35,0.6)', backdropFilter:'blur(4px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:20, width:460, maxWidth:'100%', boxShadow:'0 24px 60px rgba(0,0,0,0.28)' }}>
        <div style={{ background:'linear-gradient(135deg,#b91c1c,#e03c3c)', borderRadius:'20px 20px 0 0', padding:'20px 26px 16px', color:'#fff' }}>
          <div style={{ fontSize:18, fontWeight:800 }}>🚫 Block Company</div>
          <div style={{ fontSize:13, opacity:.85, marginTop:4 }}>"{company.name}" — cheques from this company will be rejected.</div>
        </div>
        <div style={{ padding:'22px 26px', display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#b91c1c', marginBottom:8 }}>Reason</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:7, marginBottom:10 }}>
              {BLOCK_REASONS.map(r => (
                <button key={r} type="button" onClick={() => setReason(r)}
                  style={{ padding:'5px 13px', borderRadius:30, fontSize:12.5, border: reason===r ? '2px solid #b91c1c' : '1.5px solid #e2e8f0', background: reason===r ? '#fef2f2' : '#fff', color: reason===r ? '#b91c1c' : '#475569', cursor:'pointer', fontWeight: reason===r ? 700 : 400 }}>
                  {r}
                </button>
              ))}
            </div>
            <input className="form-control" value={reason} placeholder="Or type custom reason…" onChange={e => setReason(e.target.value)} />
          </div>
          <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:10, padding:'10px 14px', display:'flex', gap:8, alignItems:'center' }}>
            <span>⚠️</span>
            <span style={{ fontSize:12.5, color:'#b91c1c' }}>All future cheques with this maker name will be automatically blocked.</span>
          </div>
        </div>
        <div style={{ padding:'14px 26px 22px', display:'flex', gap:10, justifyContent:'flex-end', borderTop:'1px solid #f1f5f9' }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button onClick={() => onBlock(company._id, reason)} disabled={saving}
            style={{ padding:'10px 22px', borderRadius:10, fontSize:14, fontWeight:700, border:'none', background:'linear-gradient(135deg,#b91c1c,#e03c3c)', color:'#fff', cursor: saving?'not-allowed':'pointer' }}>
            {saving ? '⏳ Blocking…' : '🚫 Block Company'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Main Companies page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function Companies() {
  const [companies,  setCompanies]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [total,      setTotal]      = useState(0);
  const [draft,      setDraft]      = useState('');
  const [search,     setSearch]     = useState('');
  const [filter,     setFilter]     = useState('all');
  const [page,       setPage]       = useState(1);
  const [limit,      setLimit]      = useState(10);
  const [modal,      setModal]      = useState(false);
  const [blockModal, setBlockModal] = useState(null);
  const [form,       setForm]       = useState(DEFAULT);
  const [editId,     setEditId]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [syncing,    setSyncing]    = useState(false);
  // Tracks which company row is expanded (by _id)
  const [expandedId,   setExpandedId]   = useState(null);
  const [chequeCounts, setChequeCounts] = useState({}); // companyId → cheque count

  const toggleExpand = (id) => {
    const sid = String(id);
    setExpandedId(prev => prev === sid ? null : sid);
  };

  // Load ALL cheque counts in ONE batch call (not per-row)
  const loadAllChequeCounts = useCallback(async () => {
    try {
      const res = await getChequeCounts();
      setChequeCounts(res.data.data || {});
    } catch { /* silent */ }
  }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const lastLoadTimeRef = useRef(0);
  const lastLoadParamsRef = useRef("");

  const load = useCallback(async (pg = page, lmt = limit, q = search, f = filter) => {
    const params = { page: pg, limit: lmt };
    if (q) params.search = q;
    if (f === 'blocked') params.isBlocked = 'true';
    if (f === 'active')  params.isBlocked = 'false';

    const pKey = JSON.stringify(params);
    const now = Date.now();
    if (pKey === lastLoadParamsRef.current && now - lastLoadTimeRef.current < 400) {
      return;
    }
    lastLoadParamsRef.current = pKey;
    lastLoadTimeRef.current = now;

    setLoading(true);
    try {
      const r = await getCompanies(params);
      setCompanies(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load companies'); }
    finally  { setLoading(false); }
    // Load all cheque counts in one batch (separate so companies show immediately)
    loadAllChequeCounts();
  }, [page, limit, search, filter, loadAllChequeCounts]);

  useEffect(() => { load(page, limit, search, filter); }, [page, limit, filter]); // eslint-disable-line

  const handleSearch = () => { setSearch(draft); setPage(1); load(1, limit, draft, filter); };
  const handleClear  = () => { setDraft(''); setSearch(''); setPage(1); load(1, limit, '', filter); };

  const openAdd  = () => { setEditId(null); setForm(DEFAULT); setModal(true); };
  const openEdit = c => { setEditId(c._id); setForm({ name:c.name, address:c.address||'', phone:formatPhone(c.phone||''), email:c.email||'', notes:c.notes||'' }); setModal(true); };
  const closeModal = () => { setModal(false); setEditId(null); setForm(DEFAULT); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      editId ? await updateCompany(editId, form) : await createCompany(form);
      toast.success(editId ? 'Company updated!' : `"${form.name}" added.`);
      closeModal(); load(page, limit, search, filter);
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleFullSync = async () => {
    const confirm = await Swal.fire({
      title: '🔄 Full Company Sync',
      html: `This will:<br/><br/>
        <b>1.</b> Merge duplicate company records<br/>
        <b>2.</b> Import all maker names from cheques<br/>
        <b>3.</b> Link all cheques → Company ID<br/>
        <b>4.</b> Build customers list per company<br/>
        <b>5.</b> Set employer company on each customer<br/><br/>
        Safe to run anytime — no data is deleted.`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: '▶ Run Full Sync',
      confirmButtonColor: '#1a7a4a',
      cancelButtonColor: '#6b7280',
    });
    if (!confirm.isConfirmed) return;

    setSyncing(true);
    const toastId = toast.loading('🔄 Running full sync...');
    try {
      const res = await fullSyncCompanies();
      const { stats, message, log } = res.data;
      void message;
      toast.dismiss(toastId);

      await Swal.fire({
        title: '✅ Sync Complete!',
        html: `
          <div style="text-align:left; font-size:14px; line-height:2">
            <div>🔀 <b>Duplicates merged:</b> ${stats.mergedCount}</div>
            <div>🏢 <b>Companies created:</b> ${stats.companiesCreated}</div>
            <div>📄 <b>Cheques linked:</b> ${stats.chequesLinked}</div>
            <div>👥 <b>Customer links built:</b> ${stats.customersLinked}</div>
            <div>📊 <b>Total active companies:</b> ${stats.totalActive}</div>
            ${log.length > 0 ? `<hr/><div style="font-size:12px;color:#64748b;max-height:120px;overflow-y:auto">${log.map(l => `• ${l}`).join('<br/>')}</div>` : ''}
          </div>`,
        icon: 'success',
        confirmButtonColor: '#1a7a4a',
      });

      load(page, limit, search, filter);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Sync failed', { id: toastId });
    } finally {
      setSyncing(false);
    }
  };
  void syncing;
  void handleFullSync;

  const handleDelete = async (id, name) => {
    const r = await Swal.fire({ title:'Delete Company?', html:`Remove <b>${name}</b> from the master list?`, icon:'warning', showCancelButton:true, confirmButtonText:'Yes, Delete', confirmButtonColor:'#e03c3c', cancelButtonColor:'#6b7280' });
    if (!r.isConfirmed) return;
    try { await deleteCompany(id); toast.success(`"${name}" deleted.`); load(page, limit, search, filter); }
    catch { toast.error('Delete failed'); }
  };

  const handleBlock = async (id, reason) => {
    setSaving(true);
    try {
      await blockCompany(id, reason);
      toast.success(`"${blockModal.name}" blocked.`);
      setBlockModal(null); load(page, limit, search, filter);
    } catch { toast.error('Block failed'); }
    finally { setSaving(false); }
  };

  const handleUnblock = async (id, name) => {
    const r = await Swal.fire({ title:'Unblock Company?', html:`<b>${name}</b> will be allowed for transactions again.`, icon:'question', showCancelButton:true, confirmButtonText:'✅ Yes, Unblock', confirmButtonColor:'#16a34a', cancelButtonColor:'#6b7280' });
    if (!r.isConfirmed) return;
    try { await unblockCompany(id); toast.success(`"${name}" unblocked.`); load(page, limit, search, filter); }
    catch { toast.error('Unblock failed'); }
  };


  /* ── Columns ─────────────────────────────────────────────── */
  const columns = [
    {
      name: '#', minWidth: '50px', maxWidth: '60px',
      cell: (r, i) => <span style={{ fontSize:12, color:'#94a3b8', fontFamily:'monospace' }}>{(page-1)*limit + i + 1}</span>,
    },
    {
      name: 'COMPANY NAME', selector: r => r.name, sortable: true, grow: 2,
      cell: r => (
        <div style={{ display:'flex', alignItems:'center', gap:10, width:'100%', paddingRight: 8 }}>
          <div style={{ width:36, height:36, borderRadius:9, flexShrink:0, background: r.isBlocked ? '#fef2f2' : '#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17 }}>
            {r.isBlocked ? '🚫' : '🏢'}
          </div>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontWeight:700, fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:6 }}>
              {r.isBlocked && <span title="Blocked" style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:18, height:18, borderRadius:'50%', background:'#fde68a', color:'#92400e', fontSize:11, fontWeight:900, flexShrink:0, border:'1.5px solid #f59e0b' }}>⚠</span>}
              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</span>
            </div>
            {r.isBlocked && r.blockedReason && (
              <div style={{ fontSize:11, color:'#dc2626', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>· {r.blockedReason}</div>
            )}
          </div>
          {/* Stats badges — cheques + customers */}
          <CompanyStatsBadge
            companyId={r._id}
            companyName={r.name}
            customers={r.customers || []}
            chequeCount={chequeCounts[String(r._id)] ?? null}
            expanded={expandedId === String(r._id)}
            onClick={() => toggleExpand(String(r._id))}
          />
        </div>
      ),
    },
    {
      name: 'STATUS', selector: r => r.isBlocked, sortable: true, minWidth: '120px', maxWidth: '140px',
      cell: r => r.isBlocked
        ? <span style={{ background:'#fef2f2', color:'#b91c1c', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>🚫 Blocked</span>
        : <span style={{ background:'#f0fdf4', color:'#16a34a', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>✅ Active</span>,
    },
    {
      name: 'PHONE', selector: r => r.phone, minWidth: '140px', maxWidth: '160px',
      cell: r => <span style={{ fontSize:13, color:'#475569' }}>{r.phone ? formatPhone(r.phone) : <span style={{opacity:.35}}>—</span>}</span>,
    },
    {
      name: 'ACTIONS', minWidth: '190px', maxWidth: '210px', ignoreRowClick: true,
      cell: r => (
        <div style={{ display:'flex', gap:5, flexWrap:'nowrap', alignItems:'center' }}>
          <button className="btn btn-sm btn-outline" onClick={() => openEdit(r)} style={{ padding:'4px 9px' }}>✏️</button>
          {r.isBlocked
            ? <button className="btn btn-sm" style={{ background:'#f0fdf4', color:'#16a34a', border:'1px solid #6ee7b7', fontWeight:700, fontSize:11, padding:'4px 9px' }} onClick={() => handleUnblock(r._id, r.name)}>✅ Unblock</button>
            : <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#b91c1c', border:'1px solid #fca5a5', fontWeight:700, fontSize:11, padding:'4px 9px' }} onClick={() => setBlockModal(r)}>🚫 Block</button>
          }
          <button className="btn btn-sm" style={{ background:'#f8fafc', color:'#94a3b8', border:'1px solid #e2e8f0', padding:'4px 9px' }} onClick={() => handleDelete(r._id, r.name)}>🗑️</button>
        </div>
      ),
    },
  ];

  const blockedCount = companies.filter(c => c.isBlocked).length;

  /* ── Expandable component ─────────────────────────────────── */
  const ExpandedComponent = ({ data }) => (
    <CustomerPanel customers={data.customers || []} />
  );

  return (
    <Layout title="Company Master">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:'#0f1923' }}>🏢 Company Master</h2>
          <p style={{ fontSize:13, color:'#64748b', marginTop:3 }}>
            {loading ? 'Loading…' : `${total} companies · ${blockedCount} blocked`}
            <span style={{ marginLeft:12, fontSize:12, color:'#94a3b8' }}>Used in: Customer employer · Cash a Check (Maker/Payer)</span>
          </p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => load(page, limit, search, filter)} className="btn btn-outline btn-sm" title="Refresh list">🔄</button>
          {/* Full Sync button — uncomment to enable manual sync from UI
          <button
            onClick={handleFullSync}
            disabled={syncing}
            className="btn btn-outline btn-sm"
            title="Merge duplicates, sync makers, link cheques & customers to Company Master"
            style={{ color:'var(--primary)', borderColor:'var(--primary)', fontWeight:700, display:'flex', alignItems:'center', gap:6 }}
          >
            {syncing
              ? <><span className="spinner" style={{width:13,height:13,borderWidth:2}}/> Syncing…</>
              : <>⚡ Full Sync</>
            }
          </button>
          */}
          <button onClick={openAdd} className="btn btn-primary btn-sm">🏢 + Add Company</button>
        </div>
      </div>

      {!loading && blockedCount > 0 && (
        <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:10, padding:'10px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
          <span>⚠️</span>
          <span style={{ fontSize:13, fontWeight:600, color:'#b91c1c' }}>
            {blockedCount} blocked compan{blockedCount!==1?'ies':'y'} — cheques will be rejected automatically.
          </span>
        </div>
      )}

      {/* Search + Filter */}
      <div className="card mb-4" style={{ padding:'12px 16px' }}>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <input className="form-control" placeholder="Search company name…" value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => e.key==='Enter' && handleSearch()}
            style={{ flex:1, minWidth:160 }} />
          {draft && <button className="btn btn-outline btn-sm" onClick={handleClear}>✕</button>}
          <button className="btn btn-primary btn-sm" onClick={handleSearch} style={{ fontWeight:700 }}>🔍 Search</button>
          <div style={{ display:'flex', gap:4, marginLeft:4 }}>
            {[['all','All'],['active','Active'],['blocked','Blocked 🚫']].map(([v,l]) => (
              <button key={v} onClick={() => { setFilter(v); setPage(1); load(1, limit, search, v); }}
                style={{ padding:'6px 14px', borderRadius:20, fontSize:12.5, fontWeight:600, border: filter===v ? '2px solid var(--primary)' : '1.5px solid #e2e8f0', background: filter===v ? 'var(--primary-light)' : '#fff', color: filter===v ? 'var(--primary)' : '#475569', cursor:'pointer' }}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {modal     && <CompanyModal editId={editId} form={form} setF={setF} onClose={closeModal} onSave={handleSave} saving={saving} />}
      {blockModal && <BlockModal  company={blockModal} onClose={() => setBlockModal(null)} onBlock={handleBlock} saving={saving} />}

      <div className="card">
        <DataTable
          columns={columns}
          data={companies}
          progressPending={loading}
          progressComponent={<div className="table-loading-cell"><div className="spinner spinner-dark spinner-centered"/></div>}
          noDataComponent={
            <div className="table-empty-cell">
              <div className="empty-icon">🏢</div>
              <p>{search ? `No companies match "${search}"` : 'No companies yet. Add companies to use as Employer or Maker/Payer.'}</p>
            </div>
          }
          pagination
          paginationServer
          paginationTotalRows={total}
          paginationDefaultPage={page}
          paginationPerPage={limit}
          paginationRowsPerPageOptions={[10, 20, 50]}          onChangePage={p => setPage(p)}
          onChangeRowsPerPage={(newLimit, p) => { setLimit(newLimit); setPage(p); }}
          highlightOnHover
          customStyles={dtStyles}
          expandableRows
          expandableRowExpanded={r => String(r._id) === expandedId && (r.customers?.length > 0)}
          expandableRowsComponent={ExpandedComponent}
          expandableRowsHideExpander
          onRowExpandToggled={(exp, row) => setExpandedId(exp && row.customers?.length > 0 ? String(row._id) : null)}
        />
      </div>
    </Layout>
  );
}
